import { APP_ENV } from '@/shared/config/env'
import { ApiError, NetworkError, TimeoutError, UnauthorizedError } from './errors'

/**
 * Thin fetch wrapper enforcing the BFF/cookie strategy:
 *
 *   - Always sends ``credentials: 'include'`` so HttpOnly session/refresh
 *     cookies flow with every request.
 *   - NEVER reads, stores, or attaches access/refresh tokens.
 *   - Reads the non-HttpOnly ``rager_csrf`` cookie and mirrors its value
 *     into the ``X-CSRF-Token`` header on every unsafe request
 *     (POST/PUT/PATCH/DELETE). The backend enforces double-submit.
 *   - On 401, performs a single ``/api/v1/auth/refresh`` attempt and retries
 *     the original request once. If refresh also fails, surfaces
 *     ``UnauthorizedError`` so the UI can drop back to the login screen.
 *   - Translates non-OK responses into ``ApiError`` with a readable detail,
 *     parsing JSON when possible and falling back to plain text otherwise.
 */

const BASE_URL = APP_ENV.apiBaseUrl.replace(/\/$/, '')

/**
 * Default client-side timeout (ms) applied to every request. Generous enough
 * that a healthy backend never trips it, but bounded so a hung connection can
 * never leave a mutation spinner stuck forever (delete modals, query sends).
 * On expiry the request is aborted and surfaces as a ``TimeoutError``.
 */
export const DEFAULT_TIMEOUT_MS = 30_000
/**
 * Ceiling for operations that legitimately run far longer than a CRUD call —
 * file-ingestion uploads (embedding) and RAG chat answers (LLM generation).
 * Still bounded so the UI can't hang indefinitely, just with a lot more room.
 * Sits well above the E2E upload/answer waits so it never cuts a real run.
 */
export const LONG_TIMEOUT_MS = 180_000
/** Pass ``timeoutMs: TIMEOUT_DISABLED`` to opt a single call out entirely. */
export const TIMEOUT_DISABLED = 0

const TIMEOUT_MESSAGE =
  'A solicitação demorou mais que o esperado e foi cancelada. ' +
  'Verifique sua conexão e tente novamente.'

const REFRESH_PATH = '/api/v1/auth/refresh'
const CSRF_BOOTSTRAP_PATH = '/api/v1/auth/csrf'
const CSRF_COOKIE_NAME = 'rager_csrf'
const CSRF_HEADER_NAME = 'X-CSRF-Token'
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])
// Paths that do NOT need CSRF (mirrored from backend CSRF_EXEMPT_PATHS).
const CSRF_EXEMPT_PATHS = new Set([
  '/api/v1/auth/login',
  '/api/v1/auth/csrf',
])

function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null
  const cookies = document.cookie ? document.cookie.split(';') : []
  for (const raw of cookies) {
    const eq = raw.indexOf('=')
    if (eq < 0) continue
    const name = raw.slice(0, eq).trim()
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(raw.slice(eq + 1).trim())
    }
  }
  return null
}

const csrfState: { bootstrap: Promise<void> | null } = { bootstrap: null }

async function ensureCsrfToken(): Promise<void> {
  if (readCsrfCookie()) return
  if (csrfState.bootstrap) return csrfState.bootstrap
  csrfState.bootstrap = (async () => {
    try {
      await fetch(`${BASE_URL}${CSRF_BOOTSTRAP_PATH}`, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
    } catch {
      // ignored — middleware will set the cookie on any subsequent GET
    } finally {
      csrfState.bootstrap = null
    }
  })()
  return csrfState.bootstrap
}

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export interface ApiRequestOptions {
  method?: HttpMethod
  path: string
  body?: unknown
  /** When true, body is sent as-is (FormData/Blob) without JSON serialization. */
  isMultipart?: boolean
  /** When true, skip the auto-refresh retry (used by /auth/* calls). */
  skipAuthRefresh?: boolean
  signal?: AbortSignal
  /**
   * Max time (ms) before the request is aborted with a ``TimeoutError``.
   * Defaults to ``DEFAULT_TIMEOUT_MS``. Pass ``TIMEOUT_DISABLED`` (0) to opt
   * out. A caller ``signal`` still cancels independently — whichever fires
   * first wins, and only the internal timer maps to ``TimeoutError``.
   */
  timeoutMs?: number
  /** Optional extra query params appended to the URL. */
  query?: Record<string, string | number | boolean | undefined | null>
}

interface RequestTimeout {
  /** Signal to hand to ``fetch`` — combines the caller signal + timer. */
  signal: AbortSignal | null
  /** True only when the internal timer fired (vs. caller cancellation). */
  timedOut: () => boolean
  /** Clear the timer / detach listeners. Always call in a ``finally``. */
  cleanup: () => void
}

/**
 * Build the effective abort signal for one fetch attempt: a timer that fires
 * after ``timeoutMs``, optionally chained to a caller-provided ``signal`` so
 * either can abort the request. ``timeoutMs <= 0`` disables the timer and just
 * forwards the caller signal unchanged.
 */
function startTimeout(
  external: AbortSignal | undefined,
  timeoutMs: number,
): RequestTimeout {
  if (timeoutMs <= 0) {
    return { signal: external ?? null, timedOut: () => false, cleanup: () => {} }
  }
  const controller = new AbortController()
  let timedOut = false
  const onExternalAbort = () => controller.abort()
  if (external) {
    if (external.aborted) controller.abort()
    else external.addEventListener('abort', onExternalAbort, { once: true })
  }
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    cleanup: () => {
      clearTimeout(timer)
      if (external) external.removeEventListener('abort', onExternalAbort)
    },
  }
}

interface RefreshState {
  inflight: Promise<boolean> | null
}

const refreshState: RefreshState = { inflight: null }

function buildUrl(path: string, query?: ApiRequestOptions['query']): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = new URL(`${BASE_URL}${normalizedPath}`)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue
      url.searchParams.set(key, String(value))
    }
  }
  return url.toString()
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return null
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  }
  return text
}

function extractDetail(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>
    const detail = obj.detail
    if (typeof detail === 'string' && detail.trim().length > 0) return detail
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as Record<string, unknown>
      const msg = first?.msg
      if (typeof msg === 'string') return msg
    }
    if (typeof obj.message === 'string') return obj.message
  }
  if (typeof payload === 'string' && payload.trim().length > 0) return payload
  return fallback
}

function normalizePath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`
}

function needsCsrf(method: string, path: string): boolean {
  if (!UNSAFE_METHODS.has(method.toUpperCase())) return false
  return !CSRF_EXEMPT_PATHS.has(normalizePath(path))
}

async function rawRequest(opts: ApiRequestOptions): Promise<Response> {
  const { method = 'GET', path, body, signal, query, isMultipart } = opts
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const headers: Record<string, string> = { Accept: 'application/json' }

  if (needsCsrf(method, path)) {
    let token = readCsrfCookie()
    if (!token) {
      await ensureCsrfToken()
      token = readCsrfCookie()
    }
    if (token) {
      headers[CSRF_HEADER_NAME] = token
    }
  }

  const timeout = startTimeout(signal, timeoutMs)
  const init: RequestInit = {
    method,
    credentials: 'include',
    signal: timeout.signal,
    headers,
  }
  if (body !== undefined && body !== null) {
    if (isMultipart) {
      // FormData must not be JSON-encoded, and we must NOT set
      // Content-Type — the browser appends the multipart boundary itself.
      init.body = body as BodyInit
    } else {
      init.body = typeof body === 'string' ? body : JSON.stringify(body)
      headers['Content-Type'] = 'application/json'
    }
  }
  try {
    return await fetch(buildUrl(path, query), init)
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Our timer fired → a TimeoutError the UI can show. Otherwise the
      // caller cancelled deliberately, so preserve the raw AbortError.
      if (timeout.timedOut()) throw new TimeoutError(TIMEOUT_MESSAGE, timeoutMs)
      throw err
    }
    const message =
      err instanceof Error ? err.message : 'Falha de rede ao chamar a API.'
    throw new NetworkError(message)
  } finally {
    // Clear the timer before the body is read in apiRequest(), so a late
    // tick can never abort an already-resolved response mid-parse.
    timeout.cleanup()
  }
}

async function tryRefresh(): Promise<boolean> {
  if (refreshState.inflight) return refreshState.inflight
  const promise = (async () => {
    try {
      const response = await rawRequest({
        method: 'POST',
        path: REFRESH_PATH,
        skipAuthRefresh: true,
      })
      return response.ok
    } catch {
      return false
    } finally {
      refreshState.inflight = null
    }
  })()
  refreshState.inflight = promise
  return promise
}

export async function apiRequest<T>(opts: ApiRequestOptions): Promise<T> {
  const response = await rawRequest(opts)
  if (response.ok) {
    const parsed = await parseBody(response)
    // A 2xx whose body is a string means the response was NOT JSON (parseBody
    // only returns an object when Content-Type is application/json). The usual
    // cause is the reverse proxy serving the SPA's index.html for a backend
    // path it doesn't forward (e.g. /catalog, /ingest live outside /api/*).
    // Returning HTML-as-data would make callers read undefined fields
    // (`response.items` → `undefined.map(...)`) and crash the render with no
    // boundary. Fail loudly but handled, with a diagnostic pointing at routing.
    if (typeof parsed === 'string') {
      throw new ApiError(
        `Erro ${response.status}`,
        response.status,
        `Resposta não-JSON em ${normalizePath(opts.path)} — provável rota não ` +
          `encaminhada ao backend pelo proxy reverso (confira /api, /catalog, /ingest).`,
        parsed.slice(0, 200),
      )
    }
    return parsed as T
  }

  if (response.status === 401 && !opts.skipAuthRefresh) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      const retry = await rawRequest(opts)
      if (retry.ok) return (await parseBody(retry)) as T
      const retryPayload = await parseBody(retry)
      if (retry.status === 401) {
        throw new UnauthorizedError(
          extractDetail(retryPayload, 'Sessão expirada.'),
          retryPayload,
        )
      }
      throw new ApiError(
        `Erro ${retry.status}`,
        retry.status,
        extractDetail(retryPayload, `Erro ${retry.status}`),
        retryPayload,
      )
    }
    const payload = await parseBody(response)
    throw new UnauthorizedError(
      extractDetail(payload, 'Sessão expirada.'),
      payload,
    )
  }

  const payload = await parseBody(response)
  if (response.status === 401) {
    throw new UnauthorizedError(
      extractDetail(payload, 'Não autenticado.'),
      payload,
    )
  }
  throw new ApiError(
    `Erro ${response.status}`,
    response.status,
    extractDetail(payload, `Erro ${response.status}`),
    payload,
  )
}

export const apiClient = {
  get: <T,>(path: string, query?: ApiRequestOptions['query'], signal?: AbortSignal) =>
    apiRequest<T>({ method: 'GET', path, query, signal }),
  post: <T,>(path: string, body?: unknown, opts?: Partial<ApiRequestOptions>) =>
    apiRequest<T>({ method: 'POST', path, body, ...opts }),
  postForm: <T,>(path: string, form: FormData, opts?: Partial<ApiRequestOptions>) =>
    apiRequest<T>({ method: 'POST', path, body: form, isMultipart: true, ...opts }),
  patch: <T,>(path: string, body?: unknown, opts?: Partial<ApiRequestOptions>) =>
    apiRequest<T>({ method: 'PATCH', path, body, ...opts }),
  del: <T,>(path: string, opts?: Partial<ApiRequestOptions>) =>
    apiRequest<T>({ method: 'DELETE', path, ...opts }),
}
